'use client';

// Third Party
import * as React from 'react';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';

// Utils
import { routes } from '@routes';

// UI
import { Button } from '@components/button';
import { Typography } from '@components/typography';

const vectorArt = '/assets/svg/create-account/foodAndDrinks.svg';

export const CreateAccount: React.FC = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const ref = searchParams?.get('ref') ?? undefined;
  const openSignup = () =>
    router.push(routes.signup({ ref }), { scroll: false });

  return (
    <div className="-mx-4 sm:-mx-8 mt-8">
      {/* Mobile Hero Section */}
      <section className="relative flex sm:hidden w-full h-[525px] items-center justify-between mb-4 text-primary-main">
        <Image
          src="/assets/img/heros/create-account-hero-image-mobile.jpg"
          alt="Savvy About Hero"
          fill
          className="object-fill object-center w-full h-full"
          priority
        />
        <div className="absolute inset-0 bg-black/10" />
        <div className="relative z-10 flex flex-col items-center w-full h-full p-12 pb-8 justify-between">
          <Typography
            variant="title1"
            component="h1"
            className="text-center font-medium leading-tight text-white text-4xl"
          >
            Create your free
            <br />
            account today.
          </Typography>
          <Button
            onClick={openSignup}
            size={'md'}
            className={'w-full justify-center px-8'}
          >
            Create Your Account
          </Button>
        </div>
      </section>
      {/* Desktop Hero Section */}
      <section className="hidden sm:flex relative w-full xl:rounded-2xl max-w-7xl mx-auto h-[525px] items-center justify-center overflow-hidden mb-8 lg:mb-4">
        <Image
          src="/assets/img/heros/create-account-hero-image.jpg"
          alt="Savvy About Hero"
          fill
          className="object-cover object-center w-full h-full"
          priority
        />
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 flex flex-col items-start w-full h-full px-12 pt-48 space-y-20">
          <Typography
            variant="title1"
            component="h1"
            className="text-left font-medium leading-tight text-white text-5xl"
          >
            Create your free
            <br />
            account today.
          </Typography>
          <Button onClick={openSignup} size={'md'}>
            Create Your Account
          </Button>
        </div>
      </section>
      <div className="body-container flex flex-col space-y-4 py-8">
        {/* Offer Banner */}
        <div className="w-full bg-primary-main rounded-xl flex flex-col md:flex-row items-center justify-between px-10 p-8 relative overflow-hidden">
          <div className="flex-1 z-10">
            <Typography
              variant="title1"
              component="h2"
              className="text-white text-4xl mb-2 text-center sm:text-left"
            >
              Become a Savvy traveler.
            </Typography>
            <Typography
              variant="body1"
              component="p"
              className="text-white my-4 max-w-lg text-center sm:text-left"
            >
              Create a free account to save your favorite destinations, view
              your bookings, and join our referral program where you can earn up
              to $250 in cash back toward your next stay.
            </Typography>
          </div>

          <div className=" ">
            <Image
              src={vectorArt}
              alt="Food and Drinks"
              width={256}
              height={256}
              className="my-6 sm:my-0 w-full sm:w-64"
            />
          </div>
        </div>

        {/* Stay Smarter Section */}
        <div className="flex flex-col items-center justify-between gap-2 mx-4 pt-4 sm:pt-8">
          <div className="w-full mx-auto flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-8 mb-8 sm:mb-4 sm:max-w-6xl">
            <div className="sm:w-1/2">
              <Typography
                variant="title1"
                component="h1"
                className="font-medium text-primary-main text-4xl sm:text-5xl mb-2 sm:mb-0 text-center sm:text-left"
              >
                Stay smarter.
              </Typography>
            </div>
            <div className="sm:w-1/2 flex flex-col items-start space-y-4">
              <Typography
                variant="body1"
                component="p"
                className="mb-6 max-w-xl text-primary-dark text-md text-center sm:text-left"
              >
                While others are overpaying on vacation rentals by $300-$500 in
                platform markup, Savvy travelers are booking the same properties
                for hundreds less. We&apos;ll always show you the best price
                available - no surprises at checkout, no middleman in the way.
              </Typography>
              <Button
                onClick={openSignup}
                size={'md'}
                className={'w-full justify-center px-8 sm:w-auto'}
              >
                Create Your Account
              </Button>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-8 w-full items-center justify-center">
          <div className="w-full md:w-1/2 h-120 aspect-[9/10] rounded-xl overflow-hidden relative">
            <Image
              src="/assets/img/brand-images/hot-air-balloon.jpg"
              alt="Hot air balloon"
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover"
              priority
            />
          </div>
          <div className="w-full md:w-1/2 h-120 aspect-[9/10] rounded-xl overflow-hidden relative">
            <Image
              src="/assets/img/brand-images/happy-surfboards.jpg"
              alt="Woman with sunglasses"
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover"
              priority
            />
          </div>
        </div>

        {/* $580 Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-16 mx-4 sm:max-w-6xl py-8">
          <div className="flex-1">
            <Typography
              variant="title1"
              component="h1"
              className="font-medium text-primary-main text-4xl mb-4"
            >
              What could you do
              <br />
              with an extra $400?
            </Typography>
          </div>
          <div className="flex-1 text-center sm:text-left">
            <Typography
              variant="body2"
              component="p"
              className="max-w-xl text-primary-dark text-md"
            >
              That&apos;s the beach chair upgrade you&apos;ve been debating, the
              local cooking class you&apos;ve been eyeing, or an extra night at
              the rental. Your vacation, your call. A Savvy account keeps the
              savings in your pocket and the good stuff on the itinerary.
            </Typography>
          </div>
        </div>

        {/* Final CTA Banner */}
        <div className="w-full bg-primary-xlight rounded-xl flex flex-col items-center justify-center px-6 py-10 text-center">
          <Typography
            variant="title1"
            component="h1"
            className="font-medium text-primary-main text-4xl mb-4"
          >
            Refer friends. Earn up to $250.
          </Typography>
          <Typography
            variant="body2"
            component="p"
            className="mb-6 max-w-2xl mx-auto"
          >
            Once you create your account, share Savvy with someone still
            overpaying for vacation rentals. They get $50 cash back on their
            first stay, and you earn cash back too.
          </Typography>
          <Button
            onClick={openSignup}
            size={'md'}
            className={'w-full justify-center px-8 sm:w-auto'}
          >
            Create Your Account
          </Button>
        </div>
      </div>

      <section className="mt-10 relative flex sm:hidden w-full h-[525px] items-center justify-between mb-4 text-primary-main">
        <Image
          src="/assets/img/brand-images/modern-cabin-mobile.jpg"
          alt="Modern Cabin"
          fill
          className="object-fill object-center w-full h-full"
          priority
        />
      </section>
      {/* Modern Cabin Section */}
      <section className="hidden sm:flex relative w-full xl:rounded-2xl max-w-7xl mx-auto h-[525px] items-center justify-center overflow-hidden mb-8 lg:mb-4">
        <Image
          src="/assets/img/brand-images/modern-cabin.jpg"
          alt="Modern Cabin"
          fill
          className="object-cover object-center w-full h-full"
          priority
        />
      </section>

      {/* Savvy is the best place... */}
      <section className="mx-4 sm:mx-auto flex flex-col sm:flex-row sm:items-start justify-between bg-white max-w-7xl p-4 sm:p-12 mb-8 gap-4 sm:gap-8">
        <div className="sm:w-1/2">
          <Typography
            variant="title1"
            component="h1"
            className="font-medium text-primary-main text-4xl sm:text-5xl mb-2 sm:mb-0 text-center sm:text-left sm:pr-6"
          >
            Savvy is the best place to book your next vacation rental.
          </Typography>
        </div>
        <div className="sm:w-1/2 flex flex-col items-start space-y-4">
          <Typography
            variant="body2"
            component="p"
            className="text-primary-dark text-md mb-2"
          >
            The direct-booking marketplace where travelers connect with
            professional hosts and save hundreds on every stay. No platform
            markup. No middleman. Just great properties at the best price.
          </Typography>
          <Typography
            variant="body2"
            component="p"
            className="text-primary-dark text-md mb-2 pb-4"
          >
            Create your free account and start searching for your next getaway.
          </Typography>
          <Button
            onClick={openSignup}
            size={'md'}
            className={'w-full justify-center px-8 sm:w-auto'}
          >
            Create Your Account
          </Button>
        </div>
      </section>
    </div>
  );
};
